import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Check, ArrowLeft } from 'lucide-react';
import { useShop } from '../context/ShopContext';
import { useProducts } from '../hooks/useShopify';
import { Skeleton } from "@/components/ui/skeleton";

const ProductPage = () => {
  const { id } = useParams();
  const { addToCart } = useShop();
  const { products, loading } = useProducts();

  // Find by Shopify handle (URL slug) or fallback to numeric id for mock data
  const product = products.find(p => p.handle === id || String(p.id) === String(id));

  if (loading) return <div className="container py-20"><Skeleton className="h-[500px] w-full" /></div>;
  if (!product) return <div className="container py-20 text-center">Produkt ikke funnet. <Link to="/" className="text-primary underline">Gå hjem</Link></div>;

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4">
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-primary mb-8">
          <ArrowLeft className="mr-2 h-4 w-4" /> Tilbake til butikken
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Image */}
          <div className="relative aspect-square rounded-2xl overflow-hidden bg-secondary/20 border">
            <img 
              src={product.image} 
              alt={product.name} 
              className="w-full h-full object-cover"
            />
            {product.badge && (
               <Badge className="absolute top-6 left-6 z-10 bg-accent text-white border-none px-4 py-2 text-sm shadow-md">
                 {product.badge}
               </Badge>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col h-full">
             <div className="mb-2">
                <span className="text-sm font-medium text-accent tracking-wider uppercase">{product.category}</span>
             </div>
             <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-2">
               {product.name}
             </h1>
             <p className="text-lg italic text-muted-foreground font-serif mb-6">
               {product.scientific}
             </p>
             
             <div className="flex items-baseline gap-4 mb-8">
                <span className="text-4xl font-bold text-foreground">kr {product.price},-</span>
                {product.originalPrice && (
                  <span className="text-xl text-muted-foreground line-through">kr {product.originalPrice},-</span>
                )}
             </div>

             <div className="space-y-6 mb-8">
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg w-fit">
                  <Check className="h-4 w-4" /> 
                  <span>På lager i butikk - Kan hentes i dag</span>
                </div>
                
                <p className="text-muted-foreground leading-relaxed text-lg">
                   Dette er en favoritt blant våre kunder. {product.name} trives godt i norske forhold og er nøye utvalgt av våre gartnere.
                   Perfekt for deg som ønsker en frodig og levende hage eller balkong.
                </p>

                <div className="grid grid-cols-2 gap-4 text-sm">
                   <div className="bg-secondary/10 p-4 rounded-xl">
                      <span className="block text-muted-foreground text-xs uppercase tracking-wider mb-1">Lysforhold</span>
                      <span className="font-medium text-base">Sol / Halvskygge</span>
                   </div>
                   <div className="bg-secondary/10 p-4 rounded-xl">
                      <span className="block text-muted-foreground text-xs uppercase tracking-wider mb-1">Vanning</span>
                      <span className="font-medium text-base">Jevn fuktighet</span>
                   </div>
                </div>
             </div>

             <div className="mt-auto space-y-4 pt-8 border-t">
                <Button 
                  size="lg" 
                  className="w-full bg-primary hover:bg-primary/90 text-white rounded-full text-xl h-14 shadow-lg"
                  onClick={() => addToCart(product)}
                >
                  <ShoppingCart className="mr-2 h-6 w-6" /> Legg i handlekurv
                </Button>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
